import { type MonetaryValue, add } from 'ezmoney';

import {
  type Budget,
  type RawBudget,
  reshapeRawBudget,
  getBudgetAmount,
} from './budget';
import { isActivePeriod, isFuturePeriod } from '../utils/dateTime';

export type BudgetaryExercise = {
  id: string;
  name: string;
  startDate: Date;
  endDate: Date;
  periodicity: 'monthly' | 'quarterly' | 'yearly';
  companyId: string;
  budgets: Budget[];
};

export type RawBudgetaryExercise = {
  id: string;
  name: string;
  startDate: string;
  endDate: string;
  periodicity: 'monthly' | 'quarterly' | 'yearly';
  companyId: string;
  budgets: RawBudget[];
};

export const reshapeRawBudgetaryExercise = (
  rawBudgetaryExercise: RawBudgetaryExercise,
): BudgetaryExercise => {
  return {
    id: rawBudgetaryExercise.id,
    name: rawBudgetaryExercise.name,
    startDate: new Date(rawBudgetaryExercise.startDate),
    endDate: new Date(rawBudgetaryExercise.endDate),
    periodicity: rawBudgetaryExercise.periodicity,
    companyId: rawBudgetaryExercise.companyId,
    budgets: rawBudgetaryExercise.budgets.map(reshapeRawBudget),
  };
};

export const getBudgetaryExerciseAmount = (
  budgetaryExercise: BudgetaryExercise,
): MonetaryValue | undefined => {
  return budgetaryExercise.budgets.reduce<MonetaryValue | undefined>(
    (sum, budget) => {
      const budgetAmount = getBudgetAmount(budget);
      return sum && budgetAmount ? add(sum, budgetAmount) : budgetAmount;
    },
    undefined,
  );
};

export const getCurrentOrDefaultBudgetaryExercise = (
  budgetaryExercises: BudgetaryExercise[],
): BudgetaryExercise | undefined =>
  budgetaryExercises.find(isActiveBudgetaryExercise) ?? budgetaryExercises[0];

export const getBudgetaryExercisePeriod = (
  budgetaryExercise: BudgetaryExercise,
) => ({
  start: budgetaryExercise.startDate,
  end: budgetaryExercise.endDate,
});

export const isActiveBudgetaryExercise = (
  budgetaryExercise: BudgetaryExercise,
) => isActivePeriod(getBudgetaryExercisePeriod(budgetaryExercise));

export const isFutureBudgetaryExercise = (
  budgetaryExercise: BudgetaryExercise,
) => isFuturePeriod(getBudgetaryExercisePeriod(budgetaryExercise));
